import React, { useEffect, useState } from "react";
import DoubleArrowIcon from "@/icons/withdraw/doubleArrowIcon";
import { Input } from "@/components/ui/popInput";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { tokens, networksByTokens } from "./dropdownData";
import Snippet from "./ReferralLinkSnippet";
import { useCurrencyExchange } from "@/services/currency.service";
import {
  useCreateDepositForm,
  useDepositAddressByChain,
} from "@/services/deposit.service";
import { InputAmt } from "@/components/ui/inputamt";
import { Button } from "@/components/ui/button";
import useBalanceStore from "@/stores/user/balance.store";
import { toast } from "react-toastify";

const CryptoTransfer: React.FC = () => {
  //set network based on tokens
  const { balance } = useBalanceStore();

  const [selectedTokenValue, setSelectedTokenValue] = useState(tokens[0].value);
  const [networkData, setNetworkData] = useState(
    networksByTokens[selectedTokenValue],
  );
  const [selectedNetworksValue, setSelectedNetworksValue] = useState(
    networkData[0].value,
  );
  const [showAddress, setShowAddress] = useState(false);

  useEffect(() => {
    //update network when token changes
    setNetworkData(networksByTokens[selectedTokenValue]);
    setSelectedNetworksValue(networksByTokens[selectedTokenValue][0].value);
  }, [selectedTokenValue]);

  const [usdAmount, setUSDAmount] = useState("");
  const [otherTokenAmount, setOtherTokenAmount] = useState("");

  const [depositAddress, setDepositAddress] = useState("");
  const [promoCode, setPromoCode] = useState("");
  const [tokenType, setTokenType] = useState(tokens[0].currencyType);

  // Hook to fetch deposit address
  const depositAddressMutation = useDepositAddressByChain();

  useEffect(() => {
    const fetchData = async () => {
      console.log(selectedNetworksValue);
      let chain: string;
      switch (selectedNetworksValue) {
        case "ERC20":
          chain = "ethereum";
          break;
        case "TRC20":
          chain = "ethereum";
          break;
        case "BEP20":
          chain = "binance";
          break;
        case "Solana":
          chain = "solana";
          break;
        case "Bitcoin":
          chain = "bitcoin";
          break;
        default:
          chain = "ethereum";
          break;
      }

      try {
        const response = await depositAddressMutation.mutateAsync(chain);
        const address = response.data.data.address;
        setDepositAddress(address);
      } catch (error) {
        console.error("Error occurred:", error);
      }
    };
    fetchData();
  }, [selectedNetworksValue]);

  const currencyExchangeMutation = useCurrencyExchange();

  const handleCurrencySubmit = async (
    token1: any,
    token2: any,
    amount: any,
  ) => {
    const formData = {
      amount: parseInt(amount),
      token1: token1,
      token2: token2,
    };

    try {
      const response = await currencyExchangeMutation.mutateAsync(formData);
      return response.data.data.exchangeAmount;
    } catch (error) {
      console.error("Error occurred:", error);
    }
  };

  //hook for exchange rate
  const [exchangeRateForward, setExchangeRateForward] = useState(0);
  const [exchangeRateBackwards, setExchangeRateBackwards] = useState(0);

  //get the exchange rate when user selects tokens
  useEffect(() => {
    const fetchData = async () => {
      const tokenType =
        tokens.find((token) => token.value === selectedTokenValue)
          ?.currencyType ?? "";

      //get the exchange rate
      const response = await handleCurrencySubmit("USDT", tokenType, 1);
      setTokenType(tokenType);
      setExchangeRateForward(response.token1ToToken2ExchangeAmount);
      setExchangeRateBackwards(response.token2ToToken1ExchangeAmount);
    };
    fetchData();
  }, [selectedTokenValue]);

  //change the USD based on selected token amount
  const handleOtherTokenAmountChange = (value: any) => {
    if (value === "") {
      setUSDAmount("");
      setOtherTokenAmount("");
    } else {
      const otherAmount = parseFloat(value);
      console.log("Selected token to USD : ", exchangeRateBackwards);
      setUSDAmount((otherAmount * exchangeRateBackwards).toFixed(2));
      setOtherTokenAmount(value);
    }
  };

  //change the selected token amount based on the usd
  const handleUSDAmountChange = (value: any) => {
    if (value === "") {
      setUSDAmount("");
      setOtherTokenAmount("");
    } else {
      const usdValue = parseFloat(value);
      console.log("USD to selected token : ", exchangeRateForward);
      setUSDAmount(value);
      setOtherTokenAmount((usdValue * exchangeRateForward).toFixed(2));
    }
  };

  const { mutate: submitDeposit } = useCreateDepositForm();

  if (showAddress) {
    return (
      <div className="mt-4 flex flex-col gap-4">
        <h3 className="mb-4 text-center font-[300]">
          To complete your payment, please send to the address below
        </h3>
        <div className="flex flex-col gap-2">
          <span className="text-sm font-[300]">
            Amount: {otherTokenAmount}
            {
              tokens.find((x) => x.value === selectedTokenValue)?.currencyType
            }{" "}
            (${usdAmount})
          </span>
          <span className="text-sm font-[300]">
            Network: {selectedNetworksValue}
          </span>
        </div>
        <div>
          <div className="w-full">
            <span className="text-sm font-medium text-white/50">Address</span>
            <Snippet className="mt-1 w-full rounded-md border-1 border-2 border-solid border-white/10 bg-white/5">
              {depositAddress}
            </Snippet>
          </div>
        </div>
        <Button
          className="mt-6 flex w-full  rounded-md bg-gradient-to-t from-[#1673DF] to-[#2998FF]"
          onClick={() => {
            submitDeposit(
              {
                amount: parseFloat(otherTokenAmount),
                usdAmount: parseFloat(usdAmount),
                network: (() => {
                  if (selectedNetworksValue === "ERC20") {
                    return "eth";
                  } else if (selectedNetworksValue === "BEP20") {
                    return "bsc";
                  } else if (selectedNetworksValue === "TRC20") {
                    return "tron";
                  }
                  return "";
                })(),
                token:
                  selectedTokenValue === "Ethereum" ||
                  selectedTokenValue === "Binance"
                    ? "native"
                    : "usdt",
                promoCode,
                recipientAddress: depositAddress,
              },
              {
                onSuccess: () => {
                  toast.success("Deposit form has been requested");
                },
                onError: (error: any) => {
                  toast.error(error.response.data.error || "Deposit failed!");
                },
              },
            );
          }}
        >
          I Have Completed the Payment
        </Button>
      </div>
    );
  }

  return (
    <div className="mt-2">
      <div className="flex max-sm:justify-between">
        {/* Currency Dropdown */}
        <div className="mr-2 w-full max-md:mr-3">
          <span className="text-sm font-medium text-white/50">Currency</span>
          <div className="mt-1 w-full rounded-md border-1 border-solid border-white/5">
            <Select
              value={selectedTokenValue}
              onValueChange={setSelectedTokenValue}
            >
              <SelectTrigger className="w-full border-2 border-white/5 bg-white/5 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="border-2 border-white/5 bg-[#14142A]">
                {tokens.map((item) => (
                  <SelectItem
                    key={item.value}
                    value={item.value}
                    className="w-full"
                  >
                    <div className="flex w-full items-center justify-between gap-2">
                      <div className="flex items-center">
                        <div>{item.Icon && <item.Icon size={18} />}</div>
                        <div className="ml-2 max-w-20 truncate text-xs text-white/40">
                          {item.label}
                        </div>
                      </div>
                      <div className="flex text-white">{item.currencyType}</div>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Network Dropdown */}
        <div className="w-full space-y-2">
          <span className="text-sm font-medium text-white/50">Network</span>
          <div className="mt-1 w-full rounded-md border-1 border-solid border-white/0">
            <Select
              value={selectedNetworksValue}
              onValueChange={setSelectedNetworksValue}
            >
              <SelectTrigger className="w-full border-2 border-white/5 bg-white/5 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="border-2 border-white/5 bg-[#14142A]">
                {networkData.map((item) => (
                  <SelectItem key={item.value} value={item.value}>
                    <div className="flex items-center">
                      {item.Icon && <item.Icon size={20} />}
                      <span className="ml-2 text-white">{item.label}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <div className="mt-3 w-full">
        <div className="flex w-full justify-between">
          <label
            htmlFor="amount"
            className="mb-1 block text-sm font-medium text-white/50"
          >
            Amount
          </label>
          <div className="flex flex-row gap-3">
            <label
              htmlFor="amount"
              className="mb-1 block text-sm font-medium text-white/50"
            >
              Balance
            </label>
            <label
              htmlFor="amount"
              className="mb-1 block text-sm font-medium text-white"
            >
              $
              {balance?.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </label>
          </div>
        </div>
        <div className="flex w-full flex-row items-center justify-between gap-2">
          <div className="flex w-full items-center rounded-md border-2 border-solid border-white/10 bg-white/5 pl-1 text-white">
            <Input
              type="text"
              placeholder="$0.00"
              className="w-full border-none bg-transparent text-white selection:bg-none"
              value={usdAmount}
              onChange={(e) => handleUSDAmountChange(e.target.value)}
            />
          </div>
          <div className="flex w-10 items-center rounded-md border-1 p-2">
            <DoubleArrowIcon size={20} />
          </div>
          <div className="flex w-full items-center">
            <InputAmt
              type="text"
              placeholder="0.00"
              className="w-full rounded-md border-2 border-solid border-white/10 bg-white/5 text-white"
              value={otherTokenAmount}
              beginAdornment={tokens.map((item) =>
                item.value === selectedTokenValue && item.Icon ? (
                  <div key={item.value}>{<item.Icon size={18} />}</div>
                ) : null,
              )}
              onChange={(e) => handleOtherTokenAmountChange(e.target.value)}
            />
          </div>
        </div>
        <div className="mt-2 flex w-full justify-between">
          <label
            htmlFor="amount"
            className="mb-1 block text-sm font-medium text-white/50"
          >
            1 {tokenType} ≈ {exchangeRateBackwards} USD
          </label>
        </div>
      </div>

      <div className="mb-3 mt-3 w-full">
        <label
          htmlFor="promo"
          className="mb-1 block text-sm font-medium text-white/50"
        >
          Promo code
        </label>
        <Input
          id="Promo"
          type="text"
          placeholder="PROMO_CODE"
          onChange={(e) => setPromoCode(e.target.value)}
          className="mt-1 w-full rounded-md border-2 border-solid border-white/10 bg-white/5 px-3 py-2 text-white"
        />
      </div>

      <div className="w-full">
        <span className="text-sm font-medium text-white/50">Address</span>
        <Snippet className="mt-1 w-full rounded-md border-1 border-2 border-solid border-white/10 bg-white/5">
          {depositAddress}
        </Snippet>
      </div>

      <div className="my-3 flex">
        <p className="text-xs text-gray-300 text-white/50">
          Minimum withdrawal is $10 . Transaction fee is $0.00
        </p>
      </div>
      {/* <Button */}
      {/*   className="mt-3 flex w-full rounded-md bg-gradient-to-r from-[#003BD2] to-[#00FFC2]" */}
      {/*   onClick={() => setShowAddress(true)} */}
      {/* > */}
      {/*   Proceed to Payment */}
      {/* </Button> */}
    </div>
  );
};

export default CryptoTransfer;
